import { Dialog, DialogActions, DialogContent, DialogTitle, Paper } from '@mui/material';
import { styled } from '@mui/system';
import ActionButton from 'Components/ActionButton';
import { Container, Item } from 'Components/Grid';
import { mmse } from 'neuro-schemas';
import { sortPartialDate } from 'neuro-utils';
import * as React from 'react';
import { actions } from 'Store/documents/actions';
import { actions as actionsForm } from 'Store/form';
import { useAppDispatch } from 'Store/index';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType } from 'Utility/documentHandling';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import colors from '../../../../../config/theme/colors';
import { dialogActions, dialogContent, dialogTitle } from '../../../../../config/theme/componentTheme';
import MMSEForm from '../MMSE';

import type { TRelevantDocumentType } from '../../../utils';
import type { PaperProps } from '@mui/material';
import type { TCreationPromiseType } from 'Store/documents/actions';
import type { TDispatch } from 'Store/index';

const { calculateScore } = mmse;

const StyledButton = styled('a')({
  fontSize: '1.6rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: colors.primary,
  textTransform: 'uppercase',
});

const DialogPaper = (props: PaperProps): React.JSX.Element => <Paper square={true} {...props} />;

const DEFAULT_DOCUMENTS: IDocumentWithTypeV2<'mmse'>[] = [];
const MMSEDialog = ({ documents = DEFAULT_DOCUMENTS, formData, viewing, fm }: IOwnProps): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [editingData, setEditingData] = React.useState<IMMSE>({});
  const dispatch: TDispatch = useAppDispatch();

  const isViewing = !!viewing;

  const latestMmse = filterByDocumentType(documents, 'mmse')
    .toSorted((a, b) => b._cdate - a._cdate)
    .toSorted((a, b) => sortPartialDate(b.date, a.date))
    .find(d => sortPartialDate(d.date, formData.document.date) <= 0);

  const fetchID = async (): Promise<TCreationPromiseType> => actions.createDocument('mmse', true)(dispatch);

  // Create new mmse document
  const addNewMmse = (): void => {
    fetchID()
      .then(id => {
        if (typeof id === 'object' && id._id) {
          actions.closeCommit({ id: id._id, name: 'mmse' }, editingData)(dispatch);
          dispatch(actionsForm.unsetEditing(id._id));
        }
      })
      // oxlint-disable-next-line promise/prefer-await-to-callbacks
      .catch((error: unknown) => {
        makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'MMSEDialog/addNewMmse'));
      });
  };

  return (
    <React.Fragment>
      <Container justifyContent="flex-end" alignItems="center">
        <Item xs={8}>
          {!latestMmse ? (
            fm('cognition.cerad.mmseNotFilled')
          ) : (
            <Container>
              <Item style={{ fontWeight: 600 }}>
                {latestMmse.unableToPerform
                  ? fm('cognition.unableToPerform')
                  : latestMmse.totalScoreOnly?.[0]
                    ? latestMmse.totalScore
                    : calculateScore(latestMmse)}
              </Item>
              <Item style={{ padding: '0 1rem' }}>{` (${formatPartialDate(latestMmse.date)})`}</Item>
            </Container>
          )}
        </Item>
        <Item xs={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {!isViewing && (
            <StyledButton style={{ textTransform: 'none', paddingRight: '1rem' }} onClick={() => setDialogOpen(true)}>
              {`+ ${fm('general.new')}`}
            </StyledButton>
          )}
        </Item>
      </Container>
      <Dialog open={dialogOpen} maxWidth="lg" fullWidth={true} PaperComponent={DialogPaper}>
        <DialogTitle style={dialogTitle}>{fm('cognition.mmse.mmse')}</DialogTitle>
        <DialogContent style={{ ...dialogContent, overflowX: 'hidden' }}>
          <MMSEForm
            formData={{
              onChange: (values: TOnChangeValues) => {
                setEditingData({ ...editingData, ...values });
              },
              document: editingData,
            }}
            viewing={false}
          />
        </DialogContent>
        <DialogActions style={dialogActions}>
          <StyledButton style={{ marginRight: '2rem' }} onClick={() => setDialogOpen(false)}>
            {fm('general.cancel')}
          </StyledButton>
          <ActionButton
            text="general.save"
            width={10}
            height={3}
            fontSize={16}
            onClick={() => {
              addNewMmse();
              setDialogOpen(false);
            }}
          />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
  formData: IFormData<IDocumentWithTypeV2<'cerad'>>;
  viewing?: boolean;
  fm: (id: string, values?: Record<string, (chunks: string[]) => React.JSX.Element>) => React.ReactNode[];
}

export default MMSEDialog;
